<!--
  StepFinish.vue

  Purpose: Final step of payroll wizard that displays confirmation results

  Responsibilities:
  - Display success state when period is confirmed and despesas are created
  - Display error state if confirmation failed
  - Show list of created despesas (employee payments) with amounts
  - Display totals for despesas
  - Format currency values safely (prevents vue-i18n errors)

  Data Flow:
  - Receives result object as prop with { success, error, despesas }
  - Uses useI18n for translations (t() function)
  - Uses n() function for currency formatting (with safe number conversion)

  Props:
  - result: Confirmation result object
    - success: boolean - Whether confirmation succeeded
    - error: string|null - Error message if failed
    - despesas: Array - Created despesas (employee payments)

  Used by:
  - PayrollModal wizard (finish step)

  Benefits:
  - Eliminates duplication (was duplicated 5+ times in old PayrollModal)
  - Consistent error handling and display
  - Safe currency formatting (handles NaN, null, undefined)
-->
<template>
  <div class="finish-step">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Conclusão do Período') }}</h2>
    </div>

    <!-- Success State -->
    <div v-if="result.success" class="result-success">
      <!-- Success Banner -->
      <div class="success-banner">
        <i class="pi pi-check-circle"></i>
        <div class="success-content">
          <strong>{{ t('Período Confirmado com Sucesso!') }}</strong>
          <ul>
            <li>{{ t('Todas as despesas de salário foram criadas com status "pendente"') }}</li>
            <li>{{ t('O período está pronto para pagamento através da tela de despesas') }}</li>
            <li>{{ t('Você pode fechar este assistente e prosseguir para outras tarefas') }}</li>
          </ul>
        </div>
      </div>

      <!-- Important Notes -->
      <div class="important-notes">
        <div class="notes-header">
          <i class="pi pi-info-circle"></i>
          <h3>{{ t('Próximas Ações') }}</h3>
        </div>
        <ul class="notes-list">
          <li>
            <i class="pi pi-arrow-right"></i>
            <span>{{ t('Acesse a tela de despesas para visualizar e baixar os pagamentos criados') }}</span>
          </li>
          <li>
            <i class="pi pi-arrow-right"></i>
            <span>{{ t('Você pode imprimir os comprovantes de pagamento para cada colaborador') }}</span>
          </li>
          <li>
            <i class="pi pi-arrow-right"></i>
            <span>{{ t('Os valores serão descontados da conta da franquia ao baixar as despesas') }}</span>
          </li>
        </ul>
      </div>

      <p>{{ t('Despesas foram criadas e estão disponíveis para pagamento') }}</p>

      <!-- Despesas Summary Card -->
      <div v-if="result.despesas && result.despesas.length > 0" class="result-details">
        <div class="details-header">
          <i class="pi pi-wallet"></i>
          <h4>{{ t('Despesas Criadas (Salários)') }}</h4>
        </div>
        <ul>
          <li v-for="despesa in result.despesas" :key="despesa.id">
            <strong>{{ despesa.user_name }}</strong>: {{ formatCurrency(despesa.valor) }}
          </li>
        </ul>
        <p class="total">
          <strong>{{ t('Total Despesas') }}:</strong>
          {{ formatCurrency(calculateTotalDespesas()) }}
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="result-error">
      <i class="pi pi-times-circle"></i>
      <h3>{{ t('Erro ao criar despesas') }}</h3>
      <p>{{ result.error || t('Ocorreu um erro ao processar a confirmação') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface Despesa {
  id: Id;
  user_name: string;
  valor: number | string;
}

interface ConfirmationResult {
  success: boolean;
  error: string | null;
  despesas: Despesa[];
}

interface WizardMachine {
  context: { value: { confirmationResult?: ConfirmationResult } };
}

// =============================================================================
// INJECT MACHINE - Single Source of Truth
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine')!;

// =============================================================================
// REACTIVE DATA FROM MACHINE
// =============================================================================

/**
 * Get confirmation result from machine context
 * Contains: { success, error, despesas }
 */
const result = computed((): ConfirmationResult => {
  return machine.context.value.confirmationResult ?? {
    success: false,
    error: null,
    despesas: []
  };
});

const { t, n } = useI18n();

/**
 * Helper function to safely format currency
 * Ensures we always pass a valid number to n() function
 */
const formatCurrency = (value: number | string): string => {
  const num = Number(parseFloat(String(value ?? 0))) || 0;
  if (isNaN(num)) {
    log('StepFinish', 'formatCurrency', 'Invalid number detected', { value, num });
    return n(0, 'currency');
  }
  return n(num, 'currency');
};

/**
 * Calculate total of all despesas
 */
const calculateTotalDespesas = (): number => {
  if (!result.value.despesas || !Array.isArray(result.value.despesas)) {
    return 0;
  }
  return result.value.despesas.reduce((sum: number, d: Despesa) => {
    return sum + (parseFloat(String(d.valor ?? 0)) || 0);
  }, 0);
};
</script>

<style scoped>
/* Step Header */
.step-header h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  text-align: center;
}

/* Success Banner */
.success-banner {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--green-50);
  border-left: 4px solid var(--green-500);
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.success-banner i {
  color: var(--green-500);
  font-size: 2rem;
  flex-shrink: 0;
}

.success-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--green-700);
  font-size: 1.1rem;
}

.success-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.success-content li {
  margin-bottom: 0.25rem;
  color: var(--green-900);
  font-size: 0.95rem;
}

/* Important Notes */
.important-notes {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.notes-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.notes-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.notes-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.notes-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.notes-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.5;
}

.notes-list li i {
  color: var(--primary-color);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.finish-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  gap: 1.5rem;
}

.result-success,
.result-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  max-width: 700px;
}

.result-success p {
  margin: 0;
  color: var(--text-color-secondary);
  text-align: center;
}

.result-error i {
  font-size: 4rem;
  color: var(--red-500);
}

.result-error h3 {
  margin: 0;
  color: var(--text-color);
}

.result-error p {
  margin: 0;
  color: var(--text-color-secondary);
  text-align: center;
}

.result-details {
  padding: 1.5rem;
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  width: 100%;
}

.details-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.details-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.details-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color);
}

.result-details ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.result-details li {
  padding: 0.75rem;
  background: var(--surface-card);
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.result-details li strong {
  color: var(--text-color);
}

.result-details .total {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 2px solid var(--surface-border);
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color);
}
</style>
